import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { fetchModels } from "components/main/utils/models/ModelServices";
import { ModelElementType } from "components/entities";
import { AssetIcon } from "components/common/AssetIcon";
import { createLogger } from "debug/DebugingLogger";
import { Spinner } from "components/common/Spinner";
import { Dialog } from "components/main/utils/Dialog";
const log = createLogger("ModelPicker");

type ModelPickerVariant = "popover" | "modal";

interface ModelPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (modelId: string) => void;
  /** Required for the anchored `popover` variant; ignored by `modal`. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  title?: string;
  suggestedModelIds?: string[];
  currentModelId?: string;
  stopPropagation?: boolean;
  variant?: ModelPickerVariant;
}

export const ModelPicker: React.FC<ModelPickerProps> = ({
  open,
  onClose,
  onSelect,
  anchorRef,
  title = "Choose a model:",
  suggestedModelIds = [],
  currentModelId,
  stopPropagation = false,
  variant = "popover",
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);
  const [models, setModels] = useState<ModelElementType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pos, setPos] = useState<React.CSSProperties>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const MARGIN = 8;
    const GAP = 4;
    const WIDTH = 240; // w-60
    const { innerHeight: vh, innerWidth: vw } = window;
    const spaceBelow = vh - rect.bottom - MARGIN;
    const spaceAbove = rect.top - MARGIN;
    // Flip above the trigger only when below is cramped and above is roomier —
    // otherwise a trigger near the page bottom would run off-screen.
    const openUp = spaceBelow < vh * 0.5 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(vh * 0.5, openUp ? spaceAbove : spaceBelow);
    const left = Math.max(MARGIN, Math.min(rect.left, vw - WIDTH - MARGIN));
    setPos(
      openUp
        ? { bottom: vh - rect.top + GAP, left, maxHeight }
        : { top: rect.bottom + GAP, left, maxHeight },
    );
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return;

    if (variant === "popover") updatePosition();

    setIsLoading(true);
    fetchModels(undefined, true)
      .then((fetched) => {
        const suggestedSet = new Set(suggestedModelIds);
        const owned = fetched
          .filter((m) => m.relation === "Owner")
          .sort((a, b) => {
            const aSuggested = suggestedSet.has(a.model_id);
            const bSuggested = suggestedSet.has(b.model_id);
            if (aSuggested && !bSuggested) return -1;
            if (!aSuggested && bSuggested) return 1;
            return (a.name || "").localeCompare(b.name || "");
          });
        setModels(owned);
      })
      .catch((error) => {
        log.error("Failed to fetch models:", error);
        setModels([]);
      })
      .finally(() => setIsLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open || variant !== "popover") return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    document.addEventListener("mousedown", handleClickOutside);

    const pickerEl = pickerRef.current;
    const stopNative = (e: Event) => e.stopPropagation();
    if (stopPropagation) {
      pickerEl?.addEventListener("mousedown", stopNative);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("mousedown", handleClickOutside);
      if (stopPropagation) {
        pickerEl?.removeEventListener("mousedown", stopNative);
      }
    };
  }, [open, variant, updatePosition, onClose, stopPropagation]);

  if (!open) return null;

  const suggestedSet = new Set(suggestedModelIds);
  const suggested =
    suggestedModelIds.length > 0
      ? models.filter((m) => suggestedSet.has(m.model_id))
      : [];
  const others =
    suggestedModelIds.length > 0
      ? models.filter((m) => !suggestedSet.has(m.model_id))
      : models;

  const renderModel = (model: ModelElementType) => (
    <div
      key={model.model_id}
      className="flex items-center item-height gap-2 px-1 py-1 rounded-app cursor-pointer hover:bg-list-hover"
      onClick={() => onSelect(model.model_id)}
    >
      <AssetIcon
        size="sm"
        color={model.color}
        iconIndex={model.icon}
        className="w-3! h-3!"
      />
      <span className="text-fg-regular truncate">
        {model.name || "Unnamed Model"}
      </span>
      {currentModelId && model.model_id === currentModelId && (
        <span className="text-fg-muted-medium ml-auto shrink-0">current</span>
      )}
    </div>
  );

  const list = isLoading ? (
    <div className="flex justify-center py-2">
      <Spinner />
    </div>
  ) : models.length === 0 ? (
    <div className="text-fg-muted-regular px-1 py-2 text-center">
      No owned models available
    </div>
  ) : (
    <>
      {suggested.length > 0 && (
        <>
          <div className="text-fg-muted-medium text-xs px-1 pt-1 pb-0.5">
            Suggested
          </div>
          {suggested.map(renderModel)}
        </>
      )}
      {others.length > 0 && (
        <>
          {suggested.length > 0 && (
            <div className="text-fg-muted-medium text-xs px-1 pt-2 pb-0.5">
              Other models
            </div>
          )}
          {others.map(renderModel)}
        </>
      )}
    </>
  );

  if (variant === "modal") {
    return (
      <Dialog open={open} title={title} onClose={onClose}>
        <div className="overflow-y-auto" style={{ maxHeight: "50vh" }}>
          {list}
        </div>
      </Dialog>
    );
  }

  return createPortal(
    <div
      ref={pickerRef}
      className="menu-surface fixed z-9999 w-60 p-1.5 flex flex-col"
      style={pos}
    >
      <div className="text-fg-muted-regular px-1 pb-1 shrink-0">{title}</div>
      <div className="overflow-y-auto min-h-0 scrollbar-overlay-hover">
        {list}
      </div>
    </div>,
    document.body,
  );
};
